import axios from 'axios';

import { logger } from 'packages/index';
import ENDPOINTS from 'tradera-constants/endpoints';

/**
 * Standardized api error codes
 */
const API_ERRORS = {
    IGNORE_ME: 'IGNORE_ME',
    ABORTED: 'ABORTED',
    CANCELLED: 'CANCELLED',
    NETWORK: 'NETWORK',
    TIMEOUT: 'TIMEOUT',
    VERSION_MISMATCH: 'VERSION_MISMATCH',
    LOGGED_OUT: 'LOGGED_OUT'
};

const defaultJsonRequestHeaders = {
    // Force json response
    Accept: 'application/json',
    // For WebAPI
    'X-Requested-With': 'XMLHttpRequest'
};

/**
 * Standard Axios configs
 */
const axiosConfigs = {
    // Requests to endpoints with authentication
    authenticated: {
        // Send auth cookies
        withCredentials: true,
        headers: defaultJsonRequestHeaders
    }
};

const isMissingAuthToken = responseString => {
    try {
        const responseObject = JSON.parse(responseString);
        const isMissing =
            responseObject.responseStatus.errorCode === 'MissingAuthToken';
        return isMissing;
    } catch {
        return false;
    }
};

/**
 * Create Axios instance or decorate existing instance with interceptor for expired token responses
 *
 * @param {AxiosInstance} [axiosInstance]
 * @returns {AxiosInstance}
 */
function axiosWithTokenRefresh(axiosInstance) {
    let instance;
    if (axiosInstance === undefined) {
        instance = axios.create();
    } else {
        instance = axiosInstance;
    }
    instance.interceptors.response.use(
        response => response,
        error => {
            // Intercept errors
            if (error.request) {
                const isUnauthorized = error.request.status === 401;
                if (
                    isUnauthorized ||
                    isMissingAuthToken(error.request.response)
                ) {
                    return axios
                        .get(ENDPOINTS.TOUCHWEB_URL + 'login/state')
                        .then(response => {
                            return response;
                        })
                        .then(response => response.data)
                        .then(({ isLoggedIn }) => {
                            // Redo original request with fresh token
                            if (isLoggedIn) {
                                return axios({
                                    ...error.config
                                });
                            } else {
                                // eslint-disable-next-line promise/no-return-wrap
                                return Promise.reject(API_ERRORS.LOGGED_OUT);
                            }
                        })
                        .catch(newError => {
                            // eslint-disable-next-line promise/no-return-wrap
                            return Promise.reject(newError);
                        });
                }
                return Promise.reject(error);
            }
        }
    );
    return instance;
}

const cancelTokens = {};

/**
 * Create new or return existing cancellation function
 *
 * Usage:
 * const { cancel, cancelToken } = utilizeCancelToken('tokenId')
 * if (cancel) cancel();
 * axios.get('http://api.com/...', { cancelToken });
 *
 * @param {string} tokenId - A string ID shared between requests that cancel together
 * @returns {{ cancel: function, cancelToken: function }}
 */
function utilizeCancelToken(tokenId) {
    let cancel;
    if (tokenId in cancelTokens) {
        cancel = cancelTokens[tokenId].cancel;
    }
    cancelTokens[tokenId] = axios.CancelToken.source();
    return {
        cancel,
        cancelToken: cancelTokens[tokenId].token
    };
}

/**
 * Check response version and throw error if different from current
 *
 * @param {string} version
 * @return {function(*): object|void}
 */
function checkResponseVersion(version = '12.0.0') {
    return response => {
        if (
            response.headers &&
            response.headers['X-Tradera-Version'] &&
            response.headers['X-Tradera-Version'] !== version
        ) {
            throw new Error(API_ERRORS.VERSION_MISMATCH);
        } else {
            return response;
        }
    };
}

/**
 * Checks redirect response for logged out user
 * This is an old solution. New solutions return a 401 response instead (see handleError).
 * Requires Axios config to include { withCredentials: true }
 *
 * @returns {Function}
 */
function checkResponseLoggedOut() {
    return response => {
        if (
            response.data &&
            typeof response.data === 'string' &&
            response.data.includes('Logga in')
        ) {
            throw new Error(API_ERRORS.LOGGED_OUT);
        } else {
            return response;
        }
    };
}

/**
 * Final standardized formatting of an api response
 *
 * @returns { function(*): { data: *, status: integer }}
 */
function finalizeResponse() {
    return response => {
        const { data, status } = response;
        return {
            data,
            status
        };
    };
}

/**
 * Common network request error handling
 *
 * @param {Object.<number, string>} statusHandlers - { code: message }
 * @returns {function(*=): Promise<Error>}
 */
function handleError(statusHandlers = {}) {
    return error => {
        let status = error.response && error.response.status;
        let message = error.message ? error.message : error.toString();
        if (axios.isCancel(error)) {
            error.message = API_ERRORS.CANCELLED;
        } else if (message.includes('timeout') || status === 408) {
            error.message = API_ERRORS.TIMEOUT;
        } else if (message.includes('Network')) {
            error.message = API_ERRORS.NETWORK;
        } else if (message.includes('Request aborted')) {
            error.message = API_ERRORS.ABORTED;
        } else if (status in statusHandlers) {
            error.message = statusHandlers[status];
        }
        throw error;
    };
}

function logError(error) {
    switch (error.message) {
        case API_ERRORS.IGNORE_ME:
        case API_ERRORS.CANCELLED:
        case API_ERRORS.NETWORK:
        case API_ERRORS.TIMEOUT:
            break;
        default:
            logger(error);
            break;
    }
}

export {
    API_ERRORS,
    axiosConfigs,
    axiosWithTokenRefresh,
    checkResponseVersion,
    checkResponseLoggedOut,
    defaultJsonRequestHeaders,
    finalizeResponse,
    handleError,
    logError,
    utilizeCancelToken
};
